import React, {useEffect, useState} from "react";
import {Dialog} from "primereact/dialog";
import {Dropdown} from "primereact/dropdown";
import {InputText} from "primereact/inputtext";
import Icon from "../../../system/components/icon";
import {goalProgressApi} from "../../../models/goal/goal-progress-api";
import {DECIMALS, PROJECT_MEASURES, REMINDERS, SOURCES, UNITS, errorText, formatPercent, manualPercent, parseNumber} from "./goal-progress-labels";
import "./goal-progress.css";

// «تنظیمات پیشرفت هدف» (boss's task «تغییر تنظیمات منبع پیشرفت هدف», 2026-10-26; docs/tasks/28-goal-progress-source.md):
// the progress source — subgoals, projects (by tasks, milestones or overall progress) or manual (unit, decimals,
// current / start / target, start ⇄ target) — and the status reminder.

const blankManual = {unit: "number", decimals: 0, current: "0", start: "0", target: "100"};
const asText = (value) => (value === null || value === undefined ? "" : String(value));

export default function GoalProgressSettings({visible, onHide, goal, progress, onSaved}) {
    const [source, setSource] = useState("projects");
    const [measure, setMeasure] = useState("tasks");
    const [manual, setManual] = useState(blankManual);
    const [reminder, setReminder] = useState("off");
    const [error, setError] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!visible) return;
        const settings = progress?.settings;
        setSource(settings?.source || "projects");
        setMeasure(settings?.projectMeasure || "tasks");
        setManual(settings?.manual
            ? {unit: settings.manual.unit, decimals: settings.manual.decimals, current: asText(settings.manual.current), start: asText(settings.manual.start), target: asText(settings.manual.target)}
            : {...blankManual, current: asText(progress?.percent ?? 0), unit: progress?.percent !== null && progress?.percent !== undefined ? "percent" : "number"});
        setReminder(progress?.reminder?.enabled ? progress.reminder.frequency : "off");
        setError("");
        setSaving(false);
    }, [visible, progress]);

    const setField = (field) => (event) => setManual((current) => ({...current, [field]: event.target.value}));
    const preview = manualPercent({start: parseNumber(manual.start), target: parseNumber(manual.target), current: parseNumber(manual.current)});
    const chosen = SOURCES.find((item) => item.value === source);

    const save = async () => {
        setError("");
        if (source === "manual") {
            const [start, target, current] = [manual.start, manual.target, manual.current].map(parseNumber);
            if (!Number.isFinite(start)) return setError("مقدار شروع را وارد کنید.");
            if (!Number.isFinite(target)) return setError("مقدار هدف را وارد کنید.");
            if (start === target) return setError("مقدار شروع و هدف نباید یکسان باشند.");
            if (manual.current !== "" && !Number.isFinite(current)) return setError("مقدار فعلی معتبر نیست.");
        }
        setSaving(true);
        try {
            const saved = await goalProgressApi.saveSettings(goal, {
                source,
                projectMeasure: measure,
                ...(source === "manual" ? {manual: {...manual, current: manual.current === "" ? manual.start : manual.current}} : {}),
                reminder: reminder === "off" ? {enabled: false, frequency: progress?.reminder?.frequency || "monthly"} : {enabled: true, frequency: reminder},
            });
            onSaved?.(saved);
            onHide();
        } catch (requestError) {
            setError(errorText(requestError));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog visible={visible} onHide={onHide} modal dir="rtl" className="rtl gp-settings"
                header={<div className="text-center w-full"><b className="text-base">تنظیمات پیشرفت هدف</b></div>}
                footer={(
                    <div className="flex justify-content-end gap-2">
                        <button type="button" className="gp-btn" onClick={onHide}>انصراف</button>
                        <button type="button" className="gp-btn primary" disabled={saving} onClick={save}>{saving ? "در حال ذخیره…" : "ذخیره"}</button>
                    </div>
                )}>
            <div className="gp-settings-body">
                <section>
                    <h3 className="gp-h">منبع پیشرفت</h3>
                    <div className="gp-source-grid" role="radiogroup" aria-label="منبع پیشرفت">
                        {SOURCES.map((item) => (
                            <button key={item.value} type="button" role="radio" aria-checked={source === item.value}
                                    className={`gp-source ${source === item.value ? "is-active" : ""}`} onClick={() => setSource(item.value)}>
                                <Icon icon={`mi mi:${item.icon}`} className="text-lg"/>
                                <b>{item.label}</b>
                                <span>{item.sub}</span>
                            </button>
                        ))}
                    </div>
                    <div className="mt-3">
                        <b className="text-sm">{chosen?.label}</b>
                        <p className="gp-muted m-0 mt-1">{chosen?.text}</p>
                    </div>
                </section>

                {source === "projects" && (
                    <section className="gp-box">
                        <b className="text-sm">شیوه‌ی سنجش</b>
                        <p className="gp-muted m-0 mt-1 mb-3">انتخاب کنید چه چیزی در پیشرفت این هدف حساب شود.</p>
                        <div className="flex flex-column gap-2" role="radiogroup" aria-label="شیوه‌ی سنجش پروژه‌ها">
                            {PROJECT_MEASURES.map((item) => (
                                <button key={item.value} type="button" role="radio" aria-checked={measure === item.value}
                                        className={`gp-measure ${measure === item.value ? "is-active" : ""}`} onClick={() => setMeasure(item.value)}>
                                    <span className="gp-radio"/>
                                    <Icon icon={`mi mi:${item.icon}`} className="text-base"/>
                                    <span className="flex-1 text-right"><b className="text-xs block">{item.label}</b><span className="gp-muted">{item.text}</span></span>
                                </button>
                            ))}
                        </div>
                    </section>
                )}

                {source === "subgoals" && (
                    <section className="gp-box">
                        <p className="gp-muted m-0">هر زیر هدف با منبع پیشرفت خودش سنجیده می‌شود؛ زیر هدفی که هنوز پیشرفتی ندارد در میانگین حساب نمی‌شود.</p>
                    </section>
                )}

                {source === "manual" && (
                    <section className="gp-box">
                        <div className="flex align-items-center justify-content-between gap-2 mb-3">
                            <div className="flex flex-column gap-1">
                                <b className="text-sm">سنجش</b>
                                <Dropdown value={manual.unit} options={UNITS} optionLabel="label" optionValue="value" onChange={(event) => setManual((current) => ({...current, unit: event.value}))}
                                          itemTemplate={(option) => <span className="text-xs">{option.label} <span className="gp-muted">· {option.example}</span></span>}
                                          className="text-xs gp-unit" panelClassName="rtl text-xs" aria-label="واحد سنجش"/>
                            </div>
                            <Dropdown value={manual.decimals} options={DECIMALS} optionLabel="label" optionValue="value" onChange={(event) => setManual((current) => ({...current, decimals: event.value}))}
                                      className="text-xs" panelClassName="rtl text-xs" aria-label="رقم اعشار"/>
                        </div>
                        <div className="gp-values">
                            <label className="gp-field">مقدار فعلی
                                <InputText value={manual.current} onChange={setField("current")} inputMode="decimal" className="w-full text-sm ltr-input" aria-label="مقدار فعلی"/>
                            </label>
                            <label className="gp-field"><span>مقدار شروع <b className="gp-req">*</b></span>
                                <InputText value={manual.start} onChange={setField("start")} inputMode="decimal" className="w-full text-sm ltr-input" aria-label="مقدار شروع"/>
                            </label>
                            <button type="button" className="gp-swap" title="جابه‌جا کردن شروع و هدف" aria-label="جابه‌جا کردن شروع و هدف"
                                    onClick={() => setManual((current) => ({...current, start: current.target, target: current.start}))}>
                                <Icon icon="mi mi:swap_horiz" className="text-lg"/>
                            </button>
                            <label className="gp-field"><span>مقدار هدف <b className="gp-req">*</b></span>
                                <InputText value={manual.target} onChange={setField("target")} inputMode="decimal" className="w-full text-sm ltr-input" aria-label="مقدار هدف"/>
                            </label>
                        </div>
                        <p className="gp-muted m-0 mt-3">
                            {preview === null ? "اعداد را وارد کنید تا درصد تکمیل را ببینید." : `با این اعداد، هدف ${formatPercent(preview)} تکمیل است.`}
                            {" هدفی که باید کم شود (مثل هزینه) را با جابه‌جا کردن شروع و هدف تنظیم کنید."}
                        </p>
                    </section>
                )}

                <section>
                    <h3 className="gp-h">یادآوری وضعیت</h3>
                    <div className="gp-row">
                        <div>
                            <b className="text-xs block">بازه‌ی یادآوری</b>
                            <span className="gp-muted">یادآوری منظم برای به‌روزرسانی وضعیت این هدف (برای مالک هدف)</span>
                        </div>
                        <Dropdown value={reminder} options={REMINDERS} optionLabel="label" optionValue="value" onChange={(event) => setReminder(event.value)} className="text-xs" panelClassName="rtl text-xs" aria-label="بازه‌ی یادآوری"/>
                    </div>
                </section>
                {error && <p className="text-xs gp-error m-0" role="alert">{error}</p>}
            </div>
        </Dialog>
    );
}
